/**
 * Utilitários para aplicar reajustes anuais e sazonais aos preços dos produtos
 */

export interface YearlyAdjustment {
  year: number;
  percentage: number;
}

export interface SeasonalGroup {
  name: string;
  months: number[];
  percentage: number;
}

/**
 * Calcula o multiplicador de reajuste anual para uma data específica.
 * Acumula os reajustes de todos os anos anteriores até o ano do evento.
 * Ex: se 2026 = +5% e 2027 = +10%, para um evento em 2027 o multiplicador é 1.05 * 1.10 = 1.155
 */
export const getYearlyAdjustmentMultiplier = (
  yearlyAdjustments: YearlyAdjustment[] | null | undefined,
  eventDate: Date
): number => {
  if (!yearlyAdjustments || yearlyAdjustments.length === 0) return 1;

  const eventYear = eventDate.getFullYear();
  
  // Filtrar ajustes para anos até o ano do evento
  const applicableAdjustments = yearlyAdjustments
    .filter(a => a.year <= eventYear && a.percentage !== 0)
    .sort((a, b) => a.year - b.year);

  if (applicableAdjustments.length === 0) return 1;

  // Acumular multiplicadores
  let multiplier = 1;
  for (const adj of applicableAdjustments) {
    multiplier *= (1 + adj.percentage / 100);
  }


  return multiplier;
};

/**
 * Calcula o multiplicador de sazonalidade para uma data específica.
 * Verifica se o mês do evento pertence a algum grupo sazonal.
 */
export const getSeasonalAdjustmentMultiplier = (
  seasonalGroups: SeasonalGroup[] | null | undefined,
  eventDate: Date
): number => {
  if (!seasonalGroups || seasonalGroups.length === 0) return 1;

  const eventMonth = eventDate.getMonth() + 1; // 1-12

  const matchingGroup = seasonalGroups.find(g => g.months.includes(eventMonth));

  if (!matchingGroup || matchingGroup.percentage === 0) return 1;

  const multiplier = 1 + matchingGroup.percentage / 100;

  return multiplier;
};

/**
 * Ajuste percentual por tipo de evento (ex.: Casamento +10%).
 */
export interface EventTypeAdjustment {
  type: string;
  percentage: number;
}

/** Lista padrão de tipos de evento suportados. */
export const EVENT_TYPE_OPTIONS: string[] = [
  'Casamento',
  'Corporativo',
  'Social',
  'Confraternização',
  'Aniversário',
  'Chá de Panela',
  'Chá de Cozinha',
  'Batizado',
  'Coffee Break',
  'Outros',
];

/**
 * Combina os tipos padrão com as categorias criadas pelo fornecedor
 * (`suppliers.event_type_categories`), sem duplicar nomes equivalentes.
 */
export const getEventTypeOptions = (customTypes?: unknown): string[] => {
  const list = Array.isArray(customTypes) ? customTypes : [];
  const result = [...EVENT_TYPE_OPTIONS];
  const seen = new Set(result.map(t => normalizeEventType(t)));

  list.forEach((raw) => {
    const label = typeof raw === 'string' ? raw.trim() : String((raw as any)?.label || '').trim();
    if (!label) return;
    const key = normalizeEventType(label);
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push(label);
  });

  return result;
};


const normalizeEventType = (value: string | null | undefined): string =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Calcula o multiplicador de acordo com o tipo de evento informado.
 * Sem tipo ou sem % configurado retorna 1 (nenhum acréscimo).
 */
export const getEventTypeAdjustmentMultiplier = (
  eventTypeAdjustments: EventTypeAdjustment[] | null | undefined,
  eventType: string | null | undefined
): number => {
  if (!eventTypeAdjustments || eventTypeAdjustments.length === 0) return 1;
  if (!eventType) return 1;

  const target = normalizeEventType(eventType);
  if (!target) return 1;

  const match = eventTypeAdjustments.find(a => normalizeEventType(a?.type) === target);
  const percentage = Number(match?.percentage);

  if (!match || !Number.isFinite(percentage) || percentage === 0) return 1;

  return 1 + percentage / 100;
};

/**
 * Calcula o multiplicador total combinando reajuste anual + sazonalidade + tipo de evento.
 */
export const getTotalPriceAdjustmentMultiplier = (
  yearlyAdjustments: YearlyAdjustment[] | null | undefined,
  seasonalGroups: SeasonalGroup[] | null | undefined,
  eventDate: Date | null | undefined,
  eventTypeAdjustments?: EventTypeAdjustment[] | null,
  eventType?: string | null
): number => {
  const eventTypeMultiplier = getEventTypeAdjustmentMultiplier(eventTypeAdjustments, eventType);

  if (!eventDate) return eventTypeMultiplier;

  const yearlyMultiplier = getYearlyAdjustmentMultiplier(yearlyAdjustments, eventDate);
  const seasonalMultiplier = getSeasonalAdjustmentMultiplier(seasonalGroups, eventDate);

  return yearlyMultiplier * seasonalMultiplier * eventTypeMultiplier;
};

/**
 * Aplica os ajustes de preço a um valor base.
 */
export const applyPriceAdjustments = (
  basePrice: number,
  yearlyAdjustments: YearlyAdjustment[] | null | undefined,
  seasonalGroups: SeasonalGroup[] | null | undefined,
  eventDate: Date | null | undefined,
  eventTypeAdjustments?: EventTypeAdjustment[] | null,
  eventType?: string | null
): number => {
  const multiplier = getTotalPriceAdjustmentMultiplier(
    yearlyAdjustments,
    seasonalGroups,
    eventDate,
    eventTypeAdjustments,
    eventType
  );
  return Math.round(basePrice * multiplier * 100) / 100;
};

/**
 * Parseia os dados de ajuste que podem vir como string JSON ou objeto do banco.
 */
export const parseAdjustmentData = (data: any): {
  yearlyAdjustments: YearlyAdjustment[];
  seasonalGroups: SeasonalGroup[];
  eventTypeAdjustments: EventTypeAdjustment[];
} => {
  let yearlyAdjustments: YearlyAdjustment[] = [];
  let seasonalGroups: SeasonalGroup[] = [];
  let eventTypeAdjustments: EventTypeAdjustment[] = [];

  const rawYearly = data?.yearly_adjustments ?? data?.yearlyAdjustments;
  const rawSeasonal = data?.seasonal_groups ?? data?.seasonalGroups;
  const rawEventTypes = data?.event_type_adjustments ?? data?.eventTypeAdjustments;

  if (rawYearly) {
    try {
      yearlyAdjustments = typeof rawYearly === 'string'
        ? JSON.parse(rawYearly)
        : rawYearly;
    } catch { /* ignore */ }
  }

  if (rawSeasonal) {
    try {
      seasonalGroups = typeof rawSeasonal === 'string'
        ? JSON.parse(rawSeasonal)
        : rawSeasonal;
    } catch { /* ignore */ }
  }

  if (rawEventTypes) {
    try {
      const parsed = typeof rawEventTypes === 'string'
        ? JSON.parse(rawEventTypes)
        : rawEventTypes;
      eventTypeAdjustments = Array.isArray(parsed) ? parsed : [];
    } catch { /* ignore */ }
  }

  return { yearlyAdjustments, seasonalGroups, eventTypeAdjustments };
};

/**
 * ===== Horas adicionais contratadas pelo cliente =====
 *
 * O fornecedor define, por cardápio de evento, uma duração padrão
 * (`supplier_menu.event_hour_time`) e, opcionalmente, uma tabela de horas
 * adicionais (`supplier_menu.hour_extension_tiers`).
 *
 * Cada linha da tabela é independente: representa o acréscimo TOTAL daquela
 * quantidade de horas, em percentual sobre o subtotal ou em valor fixo (R$).
 * O cliente só escolhe quantidades presentes na tabela.
 *
 * `hour_extension_percent` é legado: quando não há tabela, geramos linhas
 * lineares (1h..3h) equivalentes ao percentual por hora.
 */
export type HourExtensionMode = 'percent' | 'fixed';

export interface HourExtensionTier {
  hours: number;
  mode: HourExtensionMode;
  value: number;
}

export interface HourExtensionConfig {
  enabled: boolean;
  tiers: HourExtensionTier[];
  /** Mantido apenas para compatibilidade com dados antigos. */
  legacyPercent: number;
}

/** Normaliza uma linha da tabela vinda do banco ou do formulário. */
const normalizeTier = (raw: any): HourExtensionTier | null => {
  const hours = Number(raw?.hours);
  const value = Number(raw?.value);
  const mode: HourExtensionMode = raw?.mode === 'fixed' ? 'fixed' : 'percent';
  if (!Number.isFinite(hours) || hours <= 0) return null;
  if (!Number.isFinite(value) || value <= 0) return null;
  return { hours: Math.round(hours), mode, value };
};

/** Normaliza a configuração vinda do banco (snake_case) ou do carrinho (camelCase). */
export const parseHourExtensionConfig = (data: any): HourExtensionConfig => {
  const enabled = Boolean(data?.hour_extension_enabled ?? data?.hourExtensionEnabled);

  const rawPercent = data?.hour_extension_percent ?? data?.hourExtensionPercent;
  const percentNumber = Number(rawPercent);
  const legacyPercent = Number.isFinite(percentNumber) && percentNumber > 0 ? percentNumber : 0;

  let rawTiers = data?.hour_extension_tiers ?? data?.hourExtensionTiers;
  if (typeof rawTiers === 'string') {
    try {
      rawTiers = JSON.parse(rawTiers);
    } catch {
      rawTiers = [];
    }
  }

  let tiers: HourExtensionTier[] = Array.isArray(rawTiers)
    ? (rawTiers.map(normalizeTier).filter(Boolean) as HourExtensionTier[])
    : [];

  // Fallback para cardápios antigos que só têm o percentual linear.
  if (tiers.length === 0 && legacyPercent > 0) {
    tiers = [1, 2, 3].map((hours) => ({
      hours,
      mode: 'percent' as const,
      value: legacyPercent * hours,
    }));
  }

  // Deduplica por quantidade de horas e ordena.
  const byHours = new Map<number, HourExtensionTier>();
  tiers.forEach((tier) => byHours.set(tier.hours, tier));

  return {
    enabled,
    tiers: Array.from(byHours.values()).sort((a, b) => a.hours - b.hours),
    legacyPercent,
  };
};

/** Encontra a linha exata para a quantidade de horas escolhida. */
export const findHourExtensionTier = (
  tiers: HourExtensionTier[] | null | undefined,
  extraHours: number | null | undefined,
): HourExtensionTier | null => {
  const hours = Number(extraHours);
  if (!Array.isArray(tiers) || !Number.isFinite(hours) || hours <= 0) return null;
  return tiers.find((tier) => tier.hours === Math.round(hours)) || null;
};

/** Valor adicional (em R$) referente às horas extras escolhidas. */
export const getHourExtensionAmount = (
  subtotal: number,
  tiers: HourExtensionTier[] | null | undefined,
  extraHours: number | null | undefined,
): number => {
  const base = Number(subtotal);
  if (!Number.isFinite(base) || base < 0) return 0;

  const tier = findHourExtensionTier(tiers, extraHours);
  if (!tier) return 0;

  const amount = tier.mode === 'fixed' ? tier.value : (base * tier.value) / 100;
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return Math.round(amount * 100) / 100;
};

/** Aplica as horas extras a um subtotal já ajustado (anual/sazonal/tipo de evento). */
export const applyHourExtension = (
  subtotal: number,
  tiers: HourExtensionTier[] | null | undefined,
  extraHours: number | null | undefined,
): number => {
  const base = Number(subtotal);
  if (!Number.isFinite(base) || base <= 0) return 0;
  return Math.round((base + getHourExtensionAmount(base, tiers, extraHours)) * 100) / 100;
};


/**
 * ===== Preço especial por categoria de convidado =====
 *
 * O fornecedor define, por cardápio, categorias de convidado que pagam um
 * percentual do preço por pessoa (ex.: crianças 50%, staff 40%).
 * As quantidades informadas pelo cliente CONTAM no total de convidados usado
 * para escolher a faixa de preço.
 */
export type SpecialPricingKind = 'children' | 'staff' | 'custom';

export interface SpecialPricingTier {
  id: string;
  label: string;
  kind: SpecialPricingKind;
  /** Percentual do preço por convidado que essa categoria paga (0 = grátis). */
  percentage: number;
  minAge?: number | null;
  maxAge?: number | null;
  /** Exibir na proposta digital (mode=orcamento). */
  showInQuote: boolean;
  /** Exibir na loja aberta. */
  showInStore: boolean;
}

const randomTierId = (): string =>
  `tier_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/** Normaliza uma categoria vinda do banco ou do formulário. */
const normalizeSpecialTier = (raw: any): SpecialPricingTier | null => {
  if (!raw || typeof raw !== 'object') return null;
  const label = String(raw.label ?? '').trim();
  if (!label) return null;

  const percentage = Number(raw.percentage);
  const kind: SpecialPricingKind =
    raw.kind === 'children' || raw.kind === 'staff' ? raw.kind : 'custom';

  const parseAge = (value: any): number | null => {
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
  };

  return {
    id: String(raw.id || randomTierId()),
    label,
    kind,
    percentage: Number.isFinite(percentage) ? percentage : 0,
    minAge: parseAge(raw.minAge ?? raw.min_age),
    maxAge: parseAge(raw.maxAge ?? raw.max_age),
    showInQuote: raw.showInQuote ?? raw.show_in_quote ?? true,
    showInStore: raw.showInStore ?? raw.show_in_store ?? true,
  };
};

/** Lê `supplier_menu.special_pricing_tiers` (string JSON ou array). */
export const parseSpecialPricingTiers = (data: any): SpecialPricingTier[] => {
  let raw = data?.special_pricing_tiers ?? data?.specialPricingTiers ?? data;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeSpecialTier).filter(Boolean) as SpecialPricingTier[];
};

/** Categorias visíveis na jornada atual. */
export const getVisibleSpecialPricingTiers = (
  tiers: SpecialPricingTier[] | null | undefined,
  context: 'quote' | 'store',
): SpecialPricingTier[] => {
  if (!Array.isArray(tiers)) return [];
  return tiers.filter(t => (context === 'quote' ? t.showInQuote : t.showInStore));
};

/** Categorias sugeridas ao abrir o box pela primeira vez. */
export const createDefaultSpecialPricingTiers = (): SpecialPricingTier[] => [
  {
    id: randomTierId(),
    label: 'Crianças',
    kind: 'children',
    percentage: 50,
    minAge: 0,
    maxAge: 10,
    showInQuote: true,
    showInStore: true,
  },
  {
    id: randomTierId(),
    label: 'Staff',
    kind: 'staff',
    percentage: 50,
    minAge: null,
    maxAge: null,
    showInQuote: true,
    showInStore: true,
  },
];

export const createEmptySpecialPricingTier = (): SpecialPricingTier => ({
  id: randomTierId(),
  label: '',
  kind: 'custom',
  percentage: 50,
  minAge: null,
  maxAge: null,
  showInQuote: true,
  showInStore: true,
});

/** Soma das quantidades informadas pelo cliente. */
export const getSpecialGuestsCount = (
  quantities: Record<string, number> | null | undefined,
): number =>
  Object.values(quantities || {}).reduce((sum, qty) => {
    const n = Number(qty);
    return sum + (Number.isFinite(n) && n > 0 ? Math.round(n) : 0);
  }, 0);

/**
 * Valor ADICIONAL (R$) das categorias especiais: cada pessoa informada é um
 * item extra que custa `percentage`% do preço por convidado.
 * Elas NÃO estão incluídas no número de convidados — somam ao total.
 */
export const getSpecialPricingExtra = (
  pricePerGuest: number,
  tiers: SpecialPricingTier[] | null | undefined,
  quantities: Record<string, number> | null | undefined,
): number => {
  const ppp = Number(pricePerGuest);
  if (!Number.isFinite(ppp) || ppp <= 0 || !Array.isArray(tiers)) return 0;

  const extra = tiers.reduce((sum, tier) => {
    const qty = Number(quantities?.[tier.id]) || 0;
    if (qty <= 0) return sum;
    const pct = Number.isFinite(tier.percentage) ? tier.percentage : 100;
    const clamped = Math.min(Math.max(pct, 0), 100);
    return sum + qty * ppp * (clamped / 100);
  }, 0);

  return Math.round(extra * 100) / 100;
};

/** Linhas para exibir no resumo do total. */
export const getSpecialPricingBreakdown = (
  pricePerGuest: number,
  tiers: SpecialPricingTier[] | null | undefined,
  quantities: Record<string, number> | null | undefined,
): { id: string; label: string; quantity: number; unitPrice: number; total: number }[] => {
  const ppp = Number(pricePerGuest);
  if (!Number.isFinite(ppp) || !Array.isArray(tiers)) return [];

  return tiers
    .map((tier) => {
      const quantity = Number(quantities?.[tier.id]) || 0;
      if (quantity <= 0) return null;
      const pct = Math.min(Math.max(Number(tier.percentage) || 0, 0), 100);
      const unitPrice = Math.round(ppp * (pct / 100) * 100) / 100;
      return {
        id: tier.id,
        label: tier.label,
        quantity,
        unitPrice,
        total: Math.round(unitPrice * quantity * 100) / 100,
      };
    })
    .filter(Boolean) as { id: string; label: string; quantity: number; unitPrice: number; total: number }[];
};
